"use client"

/**
 * FloatingSheetPanel — base shell for hub-adjacent product drawers.
 *
 * Composes `Sheet` with the floating inset pattern (`showOverlay={false}`,
 * rounded rail beside the hub). Feature drawers (Export, Properties, invite,
 * folder create) supply body + footer content; this module owns chrome only.
 *
 * Reference consumer: `components/ui/export-drawer.tsx`.
 *
 * Layout contract (scroll body + pinned footer):
 * ```
 * FloatingSheetPanelContent          // flex column, overflow-hidden, fixed height
 *   FloatingSheetPanelHeader         // shrink-0
 *   <form className="flex min-h-0 flex-1 flex-col">   // optional; pins footer stack
 *     FloatingSheetPanelBody         // flex-1 min-h-0 overflow-y-auto — scrolls
 *     FloatingSheetPanelWorkflowFooter // shrink-0 — stays visible
 *   </form>
 * ```
 * Header and footer MUST NOT live inside `FloatingSheetPanelBody`.
 */

import * as React from "react"

import {
  getFloatingSheetInsetProps,
  useCompactFloatingSheet,
  type FloatingSheetSide,
} from "../../lib/floating-sheet-panel"
import { cn } from "../../lib/utils"
import { Button } from "./button"
import { Kbd, KbdGroup } from "./kbd"
import { Shortcut } from "./dropdown-menu"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "./sheet"
import { Tip } from "./tip"

/** Controlled floating sheet root — alias of `Sheet`. */
function FloatingSheetPanel(props: React.ComponentProps<typeof Sheet>) {
  return <Sheet data-slot="floating-sheet-panel" {...props} />
}

export interface FloatingSheetPanelContentProps
  extends Omit<
    React.ComponentProps<typeof SheetContent>,
    "side" | "showCloseButton" | "showOverlay"
  > {
  /** Rail edge. Default `right`. */
  side?: FloatingSheetSide
  /** Applied to `SheetContent` as `data-slot` (e.g. `export-drawer`). */
  contentSlot?: string
}

function FloatingSheetPanelContent({
  side = "right",
  className,
  style,
  contentSlot,
  children,
  ...props
}: FloatingSheetPanelContentProps) {
  const compact = useCompactFloatingSheet()
  const inset = getFloatingSheetInsetProps(side, compact)

  return (
    <SheetContent
      data-slot={contentSlot ?? "floating-sheet-panel-content"}
      side={inset.side}
      showCloseButton={false}
      showOverlay={false}
      className={cn(
        "z-[80] flex h-full max-h-full min-h-0 flex-col overflow-hidden",
        inset.className,
        className,
      )}
      style={{ ...inset.style, ...style }}
      {...props}
    >
      {children}
    </SheetContent>
  )
}

export interface FloatingSheetPanelHeaderProps {
  /** Required accessible title — renders as `SheetTitle`. */
  title: React.ReactNode
  /** Muted line under the title (Properties lifecycle label). */
  subtitle?: React.ReactNode
  /** Helper copy below the header row (`SheetDescription`). */
  description?: React.ReactNode
  onClose?: () => void
  closeLabel?: string
  className?: string
  titleClassName?: string
  /** Extra classes on the close-button `Tip` (e.g. Properties sheet z-index). */
  closeTipClassName?: string
  closeTipZIndex?: number
}

function FloatingSheetPanelHeader({
  title,
  subtitle,
  description,
  onClose,
  closeLabel = "Close",
  className,
  titleClassName,
  closeTipClassName,
  closeTipZIndex,
}: FloatingSheetPanelHeaderProps) {
  return (
    <>
      <div
        className={cn(
          "flex shrink-0 items-center justify-between gap-3 px-4 pt-5 pb-3",
          className,
        )}
      >
        <div className="min-w-0">
          <SheetTitle
            className={cn("text-base font-semibold leading-tight", titleClassName)}
          >
            {title}
          </SheetTitle>
          {subtitle ? (
            <p className="mt-0.5 truncate text-xs text-muted-foreground">{subtitle}</p>
          ) : null}
        </div>
        {onClose ? (
          <Tip
            label={closeLabel}
            side="bottom"
            contentClassName={closeTipClassName}
            contentZIndex={closeTipZIndex}
          >
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={closeLabel}
              onClick={onClose}
            >
              <i className="fa-light fa-xmark text-xs" aria-hidden="true" />
            </Button>
          </Tip>
        ) : null}
      </div>
      {description ? (
        <SheetDescription className="-mt-1 px-4 pb-3">{description}</SheetDescription>
      ) : null}
    </>
  )
}

export type FloatingSheetPanelBodyProps = React.ComponentProps<"div">

function FloatingSheetPanelBody({
  className,
  children,
  ...props
}: FloatingSheetPanelBodyProps) {
  return (
    <div
      data-slot="floating-sheet-panel-body"
      className={cn(
        "flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-y-contain",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  )
}

export type FloatingSheetPanelFooterProps = React.ComponentProps<"div">

function FloatingSheetPanelFooter({
  className,
  children,
  ...props
}: FloatingSheetPanelFooterProps) {
  return (
    <div
      data-slot="floating-sheet-panel-footer"
      className={cn(
        "mt-auto shrink-0 flex items-center gap-2 border-t border-border bg-background px-4 py-3",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  )
}

export interface FloatingSheetPanelWorkflowFooterProps {
  cancelLabel?: string
  onCancel: () => void
  primaryLabel: React.ReactNode
  /** When set, primary button uses `type="submit"` + `form={primaryForm}`. */
  primaryForm?: string
  /** Invoked by ⌘/plain Enter shortcut while the sheet is open. */
  onPrimary?: () => void
  primaryDisabled?: boolean
  primaryLoading?: boolean
  primaryLoadingLabel?: React.ReactNode
  primaryIconClassName?: string
  className?: string
}

function FloatingSheetPanelWorkflowFooter({
  cancelLabel = "Cancel",
  onCancel,
  primaryLabel,
  primaryForm,
  onPrimary,
  primaryDisabled = false,
  primaryLoading = false,
  primaryLoadingLabel = "Working…",
  primaryIconClassName = "fa-light fa-arrow-down-to-line text-xs",
  className,
}: FloatingSheetPanelWorkflowFooterProps) {
  return (
    <>
      {onPrimary ? (
        <Shortcut keys="Enter" disabled={primaryDisabled || primaryLoading} onInvoke={onPrimary} />
      ) : null}
      <FloatingSheetPanelFooter className={className}>
        <Button type="button" variant="outline" className="flex-1" onClick={onCancel}>
          {cancelLabel}
          <KbdGroup className="ms-1.5">
            <Kbd variant="bare">Esc</Kbd>
          </KbdGroup>
        </Button>
        <Button
          type={primaryForm ? "submit" : "button"}
          form={primaryForm}
          className="flex-1"
          disabled={primaryDisabled || primaryLoading}
          onClick={primaryForm ? undefined : onPrimary}
        >
          {primaryLoading ? (
            <>
              <i className="fa-light fa-spinner-third fa-spin text-xs" aria-hidden="true" />
              {primaryLoadingLabel}
            </>
          ) : (
            <>
              {primaryIconClassName ? (
                <i className={primaryIconClassName} aria-hidden="true" />
              ) : null}
              {primaryLabel}
              <KbdGroup className="ms-1.5">
                <Kbd variant="bare">⏎</Kbd>
              </KbdGroup>
            </>
          )}
        </Button>
      </FloatingSheetPanelFooter>
    </>
  )
}

export {
  FloatingSheetPanel,
  FloatingSheetPanelBody,
  FloatingSheetPanelContent,
  FloatingSheetPanelFooter,
  FloatingSheetPanelHeader,
  FloatingSheetPanelWorkflowFooter,
}

export {
  FLOATING_SHEET_CHROME_CLASS,
  FLOATING_SHEET_COMPACT_WIDTH,
  FLOATING_SHEET_DESKTOP_WIDTH,
  floatingSheetWidthClass,
  getFloatingSheetInsetProps,
  useCompactFloatingSheet,
  type FloatingSheetSide,
} from "../../lib/floating-sheet-panel"
